export const BYTES_IN_KB = 1024
export const BYTES_IN_MB = BYTES_IN_KB * BYTES_IN_KB

const MAX_AUDIO_FILE_MB = 150
const AUDIO_METADATA_TIMEOUT_MS = 5000
const SINGLE_DECIMAL_THRESHOLD = 10

const AUDIO_AIFF_MIME_TYPE = 'audio/aiff'
const AUDIO_X_AIFF_MIME_TYPE = 'audio/x-aiff'

export const MAX_AUDIO_FILE_BYTES = MAX_AUDIO_FILE_MB * BYTES_IN_MB

export const ALLOWED_AUDIO_EXTENSIONS = [
  'mp3',
  'wav',
  'flac',
  'm4a',
  'aac',
  'ogg',
  'aif',
  'aiff',
]

export const ALLOWED_AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/vnd.wave',
  'audio/flac',
  'audio/x-flac',
  'audio/mp4',
  'audio/x-m4a',
  'audio/aac',
  'audio/aacp',
  'audio/ogg',
  AUDIO_AIFF_MIME_TYPE,
  AUDIO_X_AIFF_MIME_TYPE,
]

export const AUDIO_FILE_ACCEPT = ALLOWED_AUDIO_EXTENSIONS.map(
  (extension) => `.${extension}`,
).join(',')

export const AUDIO_FILE_FORMATS_HINT =
  'MP3, WAV, FLAC, M4A, AAC, OGG, AIFF (Max 150MB)'

export type AudioFileErrorCode =
  | 'tooLarge'
  | 'unsupportedFormat'
  | 'unreadable'
  | 'noAudioTrack'
  | 'uploadFailed'

export const AUDIO_FILE_ERROR_MESSAGES: Record<AudioFileErrorCode, string> = {
  tooLarge: 'File upload failed. Track exceeds the 150 MB limit.',
  unsupportedFormat:
    'Invalid audio file. Supported formats: MP3, WAV, FLAC, M4A, AAC, OGG, AIFF.',
  unreadable:
    'Unable to read the audio file. Make sure the file is not corrupted.',
  noAudioTrack: 'The file does not contain an audio track.',
  uploadFailed: 'File upload failed. Try again or choose another file.',
}

export const getFileExtension = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf('.')
  if (dotIndex < 0) {
    return ''
  }
  return fileName.slice(dotIndex + 1).toLowerCase()
}

export const isAllowedAudioFile = (file: File): boolean => {
  const extension = getFileExtension(file.name)
  if (extension && ALLOWED_AUDIO_EXTENSIONS.includes(extension)) {
    return true
  }
  return ALLOWED_AUDIO_MIME_TYPES.includes(file.type.toLowerCase())
}

export const formatFileSize = (bytes: number): string => {
  if (bytes < BYTES_IN_KB) {
    return `${bytes} B`
  }

  const units = ['KB', 'MB', 'GB']
  let value = bytes / BYTES_IN_KB
  let unitIndex = 0

  while (value >= BYTES_IN_KB && unitIndex < units.length - 1) {
    value = value / BYTES_IN_KB
    unitIndex += 1
  }

  const fractionDigits = value < SINGLE_DECIMAL_THRESHOLD ? 1 : 0

  return `${value.toFixed(fractionDigits)} ${units[unitIndex]}`
}

export type AudioProbeResult =
  | { status: 'ok'; duration: number }
  | { status: 'empty' }
  | { status: 'error' }
  | { status: 'timeout' }
  | { status: 'unsupported' }

const AUDIO_EXTENSION_MIME_CANDIDATES: Record<string, string[]> = {
  mp3: ['audio/mpeg', 'audio/mp3'],
  wav: ['audio/wav', 'audio/x-wav', 'audio/wave'],
  flac: ['audio/flac', 'audio/x-flac'],
  m4a: ['audio/mp4', 'audio/x-m4a'],
  aac: ['audio/aac', 'audio/aacp'],
  ogg: ['audio/ogg'],
  aif: [AUDIO_AIFF_MIME_TYPE, AUDIO_X_AIFF_MIME_TYPE],
  aiff: [AUDIO_AIFF_MIME_TYPE, AUDIO_X_AIFF_MIME_TYPE],
}

const hasNativePlaybackSupport = (
  audio: HTMLAudioElement,
  file: File,
): boolean => {
  if (typeof audio.canPlayType !== 'function') {
    return true
  }

  const extension = getFileExtension(file.name)
  const candidates = [
    file.type,
    ...(AUDIO_EXTENSION_MIME_CANDIDATES[extension] || []),
  ].filter(Boolean)

  if (candidates.length === 0) {
    return true
  }

  return candidates.some((mime) => audio.canPlayType(mime) !== '')
}

export const probeAudioFile = (file: File): Promise<AudioProbeResult> =>
  new Promise((resolve) => {
    const audio = document.createElement('audio')

    if (!hasNativePlaybackSupport(audio, file)) {
      resolve({ status: 'unsupported' })
      return
    }

    const objectUrl = URL.createObjectURL(file)
    let settled = false

    const finish = (result: AudioProbeResult): void => {
      if (settled) {
        return
      }

      settled = true
      window.clearTimeout(timeoutId)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('error', handleError)
      audio.removeAttribute('src')
      URL.revokeObjectURL(objectUrl)
      resolve(result)
    }

    const handleLoadedMetadata = (): void => {
      const { duration } = audio

      if (Number.isFinite(duration) && duration > 0) {
        finish({ status: 'ok', duration })
        return
      }

      finish({ status: 'empty' })
    }

    const handleError = (): void => finish({ status: 'error' })

    const timeoutId = window.setTimeout(
      () => finish({ status: 'timeout' }),
      AUDIO_METADATA_TIMEOUT_MS,
    )

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('error', handleError)
    audio.preload = 'metadata'
    audio.src = objectUrl
  })

export type AudioFileProbe = (file: File) => Promise<AudioProbeResult>

export const validateAudioFile = async (
  file: File,
  probe: AudioFileProbe = probeAudioFile,
): Promise<AudioFileErrorCode | null> => {
  if (!isAllowedAudioFile(file)) {
    return 'unsupportedFormat'
  }

  if (file.size > MAX_AUDIO_FILE_BYTES) {
    return 'tooLarge'
  }

  const probeResult = await probe(file)

  if (probeResult.status === 'error') {
    return 'unreadable'
  }

  if (probeResult.status === 'empty') {
    return 'noAudioTrack'
  }

  return null
}
